import { isSome, Maybe } from '@crocodille/common';
import { useMemo, useState } from 'react';

import breakpointsFile from '../styles/breakpoints.json';
import { useIsomorphicLayoutEffect as useLayoutEffect } from './use-isomorphic-layout-effect';

const BREAKPOINTS = breakpointsFile.breakpoints;

/**
 * Observe changes to media query.
 *
 * * This hook will evaluate differently on server side and client side.
 * * To compensate, use the provided value to influence rendered components only
 * * in cases when the content is not shown immediately on load.
 *
 * @param mediaQuery The media query to match
 * @param fallback Fallback value which will be given on server side and on initial render.
 *
 * @returns Whether or not the media query currently matches.
 */
export function useMedia(
  mediaQuery: keyof typeof BREAKPOINTS,
  fallback?: Maybe<boolean>,
): boolean {
  const breakpoint = useMemo(
    //
    // The values are stored as css strings in the json ...
    //
    () => BREAKPOINTS[mediaQuery].replaceAll("'", ''),
    [mediaQuery],
  );

  //
  // The initial value is determined from fallback or media.
  //
  const [matches, setMatches] = useState(() => {
    if (isSome(fallback)) {
      return fallback;
    } else if (typeof window !== 'undefined') {
      return window.matchMedia(breakpoint).matches;
    } else {
      return false;
    }
  });

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      const listener = (e: MediaQueryListEvent) => setMatches(e.matches);

      const mediaQueryList = window.matchMedia(breakpoint);
      // Some browsers do not support `addEventListener` on `MediaQueryList`.
      if ('addEventListener' in (mediaQueryList as Partial<MediaQueryList>)) {
        mediaQueryList.addEventListener('change', listener);
      } else {
        mediaQueryList.addListener(listener);
      }

      setMatches(mediaQueryList.matches);

      return () => {
        // Some browsers do not support `removeEventListener` on `MediaQueryList`.
        if (
          'removeEventListener' in (mediaQueryList as Partial<MediaQueryList>)
        ) {
          mediaQueryList.removeEventListener('change', listener);
        } else {
          mediaQueryList.removeListener(listener);
        }
      };
    }
  }, [breakpoint]);

  return matches;
}
